import {
  Box,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { GfFlyerItemDto } from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../locales/messages";
import { reviewTokens } from "../theme";
import { LazyThumb } from "./LazyThumb";
import { resolvePromotionTagNames } from "./ChangesByItem/shared/promotionTagDisplay";

interface Props {
  item?: GfFlyerItemDto;
  loading: boolean;
}

const formatPriceDisplay = (
  numeric?: number | null,
  stringValue?: string | null,
): string => {
  if (numeric != null && !Number.isNaN(numeric)) return String(numeric);
  if (stringValue?.trim()) return stringValue.trim();
  return "—";
};

const heroSrc = (item?: GfFlyerItemDto): string | undefined => {
  if (!item?.gfMultiImages?.length) {
    const g = item?.gfImages?.[0];
    return g?.src;
  }
  for (const m of item.gfMultiImages) {
    const src = (m as { gfImage?: { src?: string } })?.gfImage?.src;
    if (src) return src;
  }
  return undefined;
};

const KeyValueRow = ({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) => (
  <Stack
    direction="row"
    alignItems="baseline"
    justifyContent="space-between"
    spacing={1}
    sx={{
      py: 0.75,
      borderBottom: `1px solid ${reviewTokens.border}`,
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Typography
      variant="caption"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: "text.secondary",
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontFamily: mono ? reviewTokens.fontMono : undefined,
        fontWeight: mono ? 500 : 400,
        color: "text.primary",
        textAlign: "right",
        maxWidth: "60%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Typography>
  </Stack>
);

export function FlyerItemInspectorPanel({ item, loading }: Props) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box
        component="aside"
        data-testid="item-inspector"
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: `${reviewTokens.radiusLg}px`,
          bgcolor: "background.paper",
          p: 2,
          height: "100%",
        }}
      >
        <Skeleton variant="rounded" width="100%" sx={{ aspectRatio: "1 / 1" }} />
        <Skeleton width="80%" sx={{ mt: 2 }} />
        <Skeleton width="60%" />
        <Skeleton width="50%" />
      </Box>
    );
  }

  if (!item) {
    return (
      <Box
        component="aside"
        data-testid="item-inspector"
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: `${reviewTokens.radiusLg}px`,
          bgcolor: "background.paper",
          p: 3,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack alignItems="center" spacing={1}>
          <InfoOutlinedIcon sx={{ fontSize: 28, color: "text.disabled" }} />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t(messages.Review_Select_Item_Hint())}
          </Typography>
        </Stack>
      </Box>
    );
  }

  const src = heroSrc(item);
  const categories = (item.gfFlyerItemCategory || [])
    .map((c) => c.name)
    .filter(Boolean)
    .join(", ");
  const promotionTagNames = resolvePromotionTagNames(
    item.promotionTag,
    undefined,
  );

  return (
    <Box
      component="aside"
      data-testid="item-inspector"
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: `${reviewTokens.radiusLg}px`,
        bgcolor: "background.paper",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle2">
          {t(messages.Review_Item_Details())}
        </Typography>
      </Box>

      <Box sx={{ p: 2, overflow: "auto", flex: 1 }}>
        <Box
          sx={{
            width: "100%",
            borderRadius: `${reviewTokens.radiusLg}px`,
            overflow: "hidden",
            border: `1px solid ${reviewTokens.border}`,
            mb: 2,
          }}
        >
          <LazyThumb
            src={src}
            aspectRatio={1}
            fit="contain"
            radius={reviewTokens.radiusLg}
            fallback={
              <ImageNotSupportedOutlinedIcon
                sx={{ fontSize: 36, color: "text.disabled" }}
              />
            }
          />
        </Box>

        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {item.name}
        </Typography>
        {item.nameChinese ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {item.nameChinese}
          </Typography>
        ) : null}
        {item.nameFrench ? (
          <Typography variant="body2" color="text.secondary">
            {item.nameFrench}
          </Typography>
        ) : null}

        {(item.rank != null || categories) && (
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1.25 }}>
            {item.rank != null ? (
              <Chip
                size="small"
                variant="outlined"
                label={t(messages.Item_Rank(), { rank: item.rank })}
              />
            ) : null}
            {categories ? (
              <Chip size="small" variant="outlined" label={categories} />
            ) : null}
          </Stack>
        )}

        <Divider sx={{ my: 2 }} />

        <Stack spacing={0}>
          <KeyValueRow
            label={t(messages.Unit())}
            value={item.unit?.trim() || "—"}
            mono
          />
          <KeyValueRow
            label={t(messages.Regular_Price())}
            value={formatPriceDisplay(
              item.regularPrice,
              item.regularPriceString,
            )}
            mono
          />
          <KeyValueRow
            label={t(messages.Sale_Price())}
            value={formatPriceDisplay(item.salePrice)}
            mono
          />
          <KeyValueRow
            label={t(messages.Quantity())}
            value={item.quantity != null ? String(item.quantity) : "—"}
            mono
          />
          <KeyValueRow
            label={t(messages.Rank())}
            value={item.rank != null ? String(item.rank) : "—"}
            mono
          />
          <KeyValueRow label="ID" value={item.id.slice(0, 8) + "…"} mono />
        </Stack>

        <Box sx={{ mt: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "text.secondary",
              display: "block",
              mb: 0.75,
            }}
          >
            {t(messages.Promotion_Tag())}
          </Typography>
          {promotionTagNames.length > 0 ? (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {promotionTagNames.map((name, i) => (
                <Chip
                  key={`${name}-${i}`}
                  size="small"
                  variant="outlined"
                  label={name}
                  sx={{ height: 22, maxWidth: "100%" }}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          )}
        </Box>

        <Typography
          variant="caption"
          color="text.disabled"
          display="block"
          sx={{ mt: 2 }}
        >
          {t(messages.Review_Item_Inspector_Hint())}
        </Typography>
      </Box>
    </Box>
  );
}
